/**
 * @license
 * Copyright Round Three LLC (test)
 */

import { GlobeLoader, nonMainlandList } from "./globeLoader.js"
import { StateLoader } from "./stateLoader.js"
import { GlobeControls } from "./GlobeControls.js"
import { Animation, AnimationGroup } from "./Animation.js"
import { BallotBoxPanel } from "./BallotBoxPanel.js"
import { easeOut } from "./easing.js"
import {
    CSS2DRenderer,
    CSS2DObject,
} from "three/examples/jsm/renderers/CSS2DRenderer.js"
import {
    // addCountyList,
    // collapseDropdown,
    switchToCountyList,
    scrollBottomBarToHighlighted,
    switchToStateList,
    addStateList,
    resetList,
} from "./UIHelpers.js"
import * as THREE from "three"
import {
    STATE_FIPS_BY_NAME,
    STATE_FIPS_BY_ABBR,
    STATE_INFO_BY_FIPS,
} from "./data/FIPS.js"
import { LeftPanel } from "./LeftPanel.js"
import { type LandingPageTutorial } from "./tutorial.js"
import { LoadEvent } from "./LoadEvent.js"
import MathHelpers from "./MathHelpers.js"
import {
    update as TweenUpdate,
    Tween,
    Easing,
} from "./vendor/tweenjs/tween.esm.js"
import { type PerformanceManager } from "./performanceManager.js"
import { type TextureInitializer } from "./WebMultiMaterial.js"
// import { injectFakeAO } from "./ShaderInjectors.js"
import { type Region, MapRegion } from "./Region.js"
import { dispatchFooterChangeEvent } from "./Footer"
import { LngLat, type LngLatLike } from "maplibre-gl"
import type CameraSynchronizer from "./CameraSynchronizer.js"
import {
    globeAzimuthalToLng,
    globePolarToLat,
    latToGlobePolar,
    lngToGlobeAzimuthal,
} from "./CameraSynchronizer.js"
import {
    addTweenGroup,
    getRegion,
    getTweenGroup,
    isFace,
    isMapGroup,
    isMapMesh,
    isTopBorder,
    transferScale,
    unGrayAll,
} from "./ThreeObjectUtils.js"
import { type GLTF } from "three/examples/jsm/loaders/GLTFLoader.js"
import React from "react"
import * as ReactDOM from "react-dom/client"
import TapHelperLabel, {
    type TapHelperProps,
    TapHelperLabelKind,
    TwoPointPin,
} from "./components/TapHelperLabel.jsx"
import { DEG2RAD } from "three/src/math/MathUtils.js"
import {
    ensureValidFIPS,
    fipsToStateAndCounty,
    getStateFIPS,
} from "./FIPSUtilities.js"
import {
    type HistoryState,
    HistoryStateType,
    type GlobeHistoryState,
    historyStateToURL,
    type InitialHistoryState,
    updateHistory,
    getPrevHistoryState,
    checkPopstateInaction,
    notifyPopstateInaction,
    setPrevHistoryState,
    urlToHistoryState,
    globeHistoryState,
    initialHistoryState,
} from "./GlobeHistory.js"

export const mapTilerApi =
    "https://api.maptiler.com/maps/ad5c3dd1-fb81-4d7e-8e51-dc6a0e3a60df/static/"
export const mapTilerKey = "1NF19JtA2hQtGW5PyEp2"

export enum LayerKind {
    COUNTRIES = 0,
    STATES,
    COUNTIES,
    INDIVIDUAL_COUNTY,
}

interface MapPinDatum {
    layer: LayerKind
    pinKind: TapHelperLabelKind
    label: string
    fips: FIPSCode
    onlyWhileRaised?: string
    exclude?: string[]
    angularThreshold: AngleDegrees
    distanceThreshold: { near: number; far: number }
}

interface LoadCountiesOptions {
    modifyHistory?: boolean
    modifyTutorial?: boolean
    signal?: AbortSignal
}

interface LoadZipsOptions {
    modifyHistory?: boolean
    modifyTutorial?: boolean
    signal?: AbortSignal
}

export function fipsToLayerKind(fips: FIPSCode): LayerKind {
    if (fips.length === 2) {
        return LayerKind.COUNTIES
    } else if (fips.length === 5) {
        return LayerKind.INDIVIDUAL_COUNTY
    }
    throw new Error(`invalid fips code ${fips}`)
}

export class Globe extends EventTarget {
    /**
     * The parent group to contain all globe objects.
     */
    scene: THREE.Scene
    /**
     * The canvas element rendering the globe.
     */
    canvas: HTMLCanvasElement
    resetButton: HTMLElement
    gpuTier: any
    settings: any
    state: {
        // TODO: Possibly add more zoom levels - 2 for county level, 3 for zip level, etc.
        /**
         * 0 is initial/completely zoomed out, 1 is state level
         * @type {number}
         */
        zoomLevel: number
        /**
         * @type {boolean}
         */
        aStateHasBeenClicked: boolean
        /**
         * @type {boolean}
         */
        resetButtonShown: boolean
        /**
         * @type {{holePunchedRegion: Region, closeEnough: Boolean}}
         */
        map: { region: MapRegion | null; closeEnough: boolean }
    }

    center: THREE.Vector3
    _tempSpherical: THREE.Spherical
    _tempVector3: THREE.Vector3
    _temp2Vector3: THREE.Vector3
    /**
     * Color of lowered regions
     */
    lowerColor: THREE.Color
    /**
     * Emissive color of lowered regions
     */
    lowerEmissive: THREE.Color
    unfadeColor: THREE.Color
    unfadeEmissive: THREE.Color
    materials: { holePunch: THREE.MeshLambertMaterial }
    tutorial: LandingPageTutorial
    mouseDownBool: boolean
    pointerXOnPointerDown: number
    pointerYOnPointerDown: number
    pointerDragDistance: number
    pointerDelta: THREE.Vector2
    pointer: THREE.Vector2
    mostRecentLoad: null | LoadEvent
    historyStatePushed: boolean
    group: THREE.Group
    leftPanel: LeftPanel
    labelRenderer: CSS2DRenderer
    RAYCAST_INTERSECTABLE: THREE.Object3D[]
    CURSOR_POS_3D: THREE.Vector3
    ANIMATIONS: AnimationGroup
    raycaster: THREE.Raycaster
    SELECTED_ZIP: string
    base_fov: number
    camera: THREE.PerspectiveCamera
    controls: GlobeControls
    nav_list: any
    INTERSECTED: null | THREE.Mesh
    CLICKED: null | THREE.Mesh
    INDIVIDUAL_COUNTY: null | Region
    COUNTY_GROUP: THREE.Group
    STATE_GROUP: THREE.Group
    CITY_GROUP: THREE.Group
    ZIP_GROUP: null | THREE.Group
    WATER: THREE.Group
    USA: THREE.Group
    COUNTRIES: THREE.Group
    textureInitializer: TextureInitializer
    globeLoader: GlobeLoader
    stateLoader: StateLoader
    ballot_screenshot_mode: string | null
    addStateListPromise: Promise<any>
    loadStatesPromise: Promise<any>
    loadUSAPromiseResolve: (value: unknown) => void
    loadUSAPromiseReject: (value: unknown) => void
    loadUSAPromise: Promise<unknown>
    loadCountriesPromise: Promise<any>
    loadCountiesPromise: undefined | Promise<any>
    globe_svg: HTMLElement | null
    globe_svg_g: any
    globe_svg_container: any
    globe_ring: HTMLElement | null
    BALLOT_BOXES: BallotBoxPanel
    pointerUpListener: (event: any) => void
    cameraSynchronizer: CameraSynchronizer

    mapPinData: MapPinDatum[] = [
        {
            layer: LayerKind.STATES,
            pinKind: TapHelperLabelKind.Vertical,
            label: "DC",
            fips: "11",
            exclude: ["DC"],
            angularThreshold: 13 * DEG2RAD,
            distanceThreshold: { near: 0, far: 90 },
        },
        {
            layer: LayerKind.COUNTIES,
            pinKind: TapHelperLabelKind.Vertical,
            label: "DC",
            fips: "11",
            exclude: ["MD", "VA", "DC"],
            angularThreshold: 13 * DEG2RAD,
            distanceThreshold: { near: 0, far: 90 },
        },
        {
            layer: LayerKind.COUNTIES,
            pinKind: TapHelperLabelKind.Left,
            label: "DC",
            fips: "11",
            onlyWhileRaised: "MD",
            angularThreshold: 13 * DEG2RAD,
            distanceThreshold: { near: 0, far: 90 },
        },
        {
            layer: LayerKind.COUNTIES,
            pinKind: TapHelperLabelKind.Right,
            label: "DC",
            fips: "11",
            onlyWhileRaised: "VA",
            angularThreshold: 13 * DEG2RAD,
            distanceThreshold: { near: 0, far: 90 },
        },
    ]

    mapPins: Array<{
        obj: TwoPointPin | CSS2DObject
        data: MapPinDatum & { region: Region }
        root?: ReactDOM.Root
        onClick: () => void
    }> = []

    get statesLoaded(): boolean {
        return (
            this.STATE_GROUP !== undefined &&
            this.STATE_GROUP.children.length > 0
        )
    }

    get currentLayerKind(): LayerKind {
        if (this.state.zoomLevel === 0) {
            return LayerKind.COUNTRIES
        }
        if (!this.areCountiesLoaded()) {
            return LayerKind.STATES
        }
        if (!this.areZipsLoaded()) {
            return LayerKind.COUNTIES
        }
        return LayerKind.INDIVIDUAL_COUNTY
    }

    get currentLayerGroup(): THREE.Group | THREE.Mesh {
        const kind = this.currentLayerKind
        switch (kind) {
            case LayerKind.COUNTRIES:
                return this.COUNTRIES
            case LayerKind.STATES:
                return this.STATE_GROUP
            case LayerKind.COUNTIES:
                return this.COUNTY_GROUP
            case LayerKind.INDIVIDUAL_COUNTY:
                if (this.INDIVIDUAL_COUNTY === null) {
                    throw new Error(
                        "currentLayerKind is INDIVIDUAL_COUNTY but this.INDIVIDUAL_COUNTY was null"
                    )
                }
                return this.INDIVIDUAL_COUNTY.face
        }
    }

    get highlightedLabel(): THREE.Object3D<THREE.Event> | null | undefined {
        if (this.INTERSECTED == null) {
            return null
        }
        return getRegion(this.INTERSECTED).label
    }

    /**
     * Update the internal state of the globe to match a HistoryState
     * @param historyState
     */
    async setFromHistoryState(
        historyState: HistoryState,
        modifyHistory = false
    ): Promise<void> {
        const prevHistoryState = getPrevHistoryState()
        console.log(
            { historyState, prevHistoryState, modifyHistory },
            "set from history state"
        )
        // Branch by HistoryState type
        if (historyState.type === HistoryStateType.initial) {
            this.reset(modifyHistory)
        } else if (historyState.type === HistoryStateType.globe) {
            await this.flyTo(historyState, modifyHistory)
        } else if (historyState.type === HistoryStateType.map) {
            const lat = historyState.lat
            const lon = historyState.lon
            const usCounty = historyState.usCounty
            const usState = historyState.usState
            window
                .smoothlyGoToMapPoint(lat, lon, 13.24, usCounty, usState, {
                    modifyHistory,
                })
                .catch((err) => {
                    console.warn(err)
                })
        }
    }

    /**
     * Set globe state based on a GlobeHistoryState
     * @param historyState
     */
    async flyTo(
        historyState: GlobeHistoryState,
        modifyHistory = true
    ): Promise<void> {
        const fips = historyState.fips
        const prevHistoryState = getPrevHistoryState()
        try {
            ensureValidFIPS(fips)
        } catch (err) {
            console.error({ err }, "fips was not valid")
        }
        if (prevHistoryState?.type === HistoryStateType.map) {
            this.cameraSynchronizer.master = "globe"
            this.cameraSynchronizer.syncGlobe()
            this.animate(100000000)
        }
        // Globe -> Globe
        const toLayer = fipsToLayerKind(historyState.fips)
        const { usState, usCounty } = fipsToStateAndCounty(fips)
        if (toLayer === LayerKind.COUNTIES) {
            await this.loadCounties(usState, true, false)
        } else if (toLayer === LayerKind.INDIVIDUAL_COUNTY) {
            if (usCounty === undefined) {
                console.error("fips code was not a county code")
                return
            }
            const promises = this.loadCountiesWithRaisePromise(usState, false, {
                modifyHistory,
                modifyTutorial: true,
            })
            await Promise.all(promises)
            const countyMesh = this.getCountyByName(usCounty)
            if (countyMesh === undefined) {
                console.error("county mesh could not be found")
                return
            }
            await this.loadZips(countyMesh, { modifyHistory })
        }
    }

    /**
     * Load all globe resources at a point down to the specified layer
     *
     * Equivalent to navigating to that point, but without the camera changes
     */
    async load(
        pos: LngLatLike,
        toLayer: LayerKind = LayerKind.INDIVIDUAL_COUNTY
    ): Promise<void> {
        const lngLat = LngLat.convert(pos)

        // Convert from latitude/longitude to globe spherical
        const polar = latToGlobePolar(lngLat.lat)
        const azimuthal = lngToGlobeAzimuthal(lngLat.lng)

        // Direction to the center of the globe
        const direction = new THREE.Vector3()
            .setFromSpherical(new THREE.Spherical(1, polar, azimuthal))
            .negate()

        const eulerPos = new THREE.Vector3()
            .setFromSpherical(new THREE.Spherical(60, polar, azimuthal))
            .sub(new THREE.Vector3(0, 0, 50))

        let toRaycast: THREE.Object3D[]
        if (this.currentLayerGroup instanceof THREE.Mesh) {
            toRaycast = [this.currentLayerGroup]
        } else {
            toRaycast = this.currentLayerGroup.children
        }

        this.raycaster.set(eulerPos, direction)
        this.raycaster.intersectObjects(toRaycast)
    }

    /**
     * Navigate instantly to a point on the globe
     *
     * Resolves when the lowest level has been loaded
     */
    async jumpTo(pos: LngLatLike): Promise<void> {
        const lngLat = LngLat.convert(pos)
        await this.load(lngLat)
        this.controls.focus(lngLat.lng, lngLat.lat)
        // Todo: The rest of the camera params
        // Zoom
        // minDistance
        // maxDistance
        // focusRadius
        // startRotating
        // endRotating
    }

    getStateByName(name: string): THREE.Mesh {
        // Replace any spaces with _
        // name = name.replace(/ /g, "_");
        const nChildren = this.STATE_GROUP.children.length
        for (let i = 0; i < nChildren; i++) {
            const state = this.STATE_GROUP.children[i]
            if (state.userData.name === name) {
                if (!(state instanceof THREE.Mesh)) {
                    throw new Error("State object was not a mesh")
                }
                return state
            }
        }
        throw new Error(`No state with name ${name}`)
    }

    // Updates the intersectable array to contain all objects in the scene with labels.
    // Should be called after any modification of the scene that may have added or removed an object with a label.
    // TODO: Could be further optimized by only adding and removing objects that changed rather than clearing the whole array
    updateIntersectable(): void {
        // Clear out the old array
        this.RAYCAST_INTERSECTABLE = []

        if (this.areCountiesLoaded()) {
            this.COUNTY_GROUP.traverse((obj) => {
                if (this.getLabel(obj) === null) return

                if (!obj.visible) return

                this.RAYCAST_INTERSECTABLE.push(obj)
            })
        }
        if (this.statesLoaded && this.state.zoomLevel === 1) {
            this.STATE_GROUP.traverse((obj) => {
                if (this.getLabel(obj) === null) return

                if (!obj.visible) return

                this.RAYCAST_INTERSECTABLE.push(obj)
            })
        }
        if (this.statesLoaded && this.state.zoomLevel === 0) {
            this.STATE_GROUP.traverse((obj) => {
                if (this.getLabel(obj) === null) return

                if (!obj.visible) return

                if (!nonMainlandList.includes(obj.userData.abbr)) return

                this.RAYCAST_INTERSECTABLE.push(obj)
            })
        }
        if (this.state.zoomLevel === 0) {
            this.USA.traverse((obj) => {
                if (this.getLabel(obj) === null) return

                if (!obj.visible) return

                this.RAYCAST_INTERSECTABLE.push(obj)
            })
        }
        if (this.areZipsLoaded() && this.INDIVIDUAL_COUNTY !== null) {
            this.RAYCAST_INTERSECTABLE.push(this.INDIVIDUAL_COUNTY.face)
        }
        this.RAYCAST_INTERSECTABLE.push(this.WATER)
    }

    getLabel(mesh: THREE.Object3D): null | CSS2DObject {
        if (!(mesh instanceof THREE.Mesh)) return null

        let label: CSS2DObject | null = null
        mesh.traverse((obj: THREE.Object3D) => {
            if (obj instanceof CSS2DObject) {
                label = obj
            }
        })
        return label
    }

    areZipsLoaded(): boolean {
        return (
            this.INDIVIDUAL_COUNTY !== null && !this.INDIVIDUAL_COUNTY.disposed
        )
    }

    areCountiesLoaded(): boolean {
        return this.COUNTY_GROUP.children.length !== 0
    }

    areCountriesLoaded(): boolean {
        return this.COUNTRIES.children.length !== 0
    }

    attachMapPins(layer: LayerKind): void {
        // Select only pins on this layer
        this.mapPinData
            .filter((pin) => pin.layer === layer)
            .forEach((pin) => {
                // Create pin container
                const container = document.createElement("div")
                container.classList.add("tab-helper-root")
                container.style.position = "absolute"
                this.labelRenderer.domElement.appendChild(container)

                const bubbleContainer = document.createElement("div")
                bubbleContainer.classList.add("tab-helper-root")
                bubbleContainer.style.position = "absolute"
                // bubbleContainer.style.zIndex = "19"
                this.labelRenderer.domElement.appendChild(bubbleContainer)
                // Look up mesh associated with the pin
                let mesh: THREE.Mesh
                switch (layer) {
                    case LayerKind.STATES:
                        mesh = this.getStateByFIPS(pin.fips)
                        break

                    default:
                        mesh = this.getStateByFIPS(pin.fips)
                    // throw new Error("unsupported layer type")
                }

                const onClick = (): void => {
                    this.state.aStateHasBeenClicked = true
                    void this.loadCounties(mesh, true, true)
                }

                if (pin.pinKind === TapHelperLabelKind.Vertical) {
                    const tabHelperRoot = ReactDOM.createRoot(container)
                    tabHelperRoot.render(
                        React.createElement(TapHelperLabel, {
                            label: "DC",
                            onClick,
                            kind: pin.pinKind,
                            size: 200,
                        })
                    )
                    // Create pin CSS2DOjbect
                    const obj = new CSS2DObject(container)
                    obj.userData.labelType = "taphelper"
                    this.getChildAnchor(mesh)?.add(obj)
                    this.mapPins.push({
                        obj,
                        data: { ...pin, region: getRegion(mesh) },
                        root: tabHelperRoot,
                        onClick,
                    })
                } else if (
                    pin.pinKind === TapHelperLabelKind.Left ||
                    pin.pinKind === TapHelperLabelKind.Right
                ) {
                    const point1 =
                        pin.pinKind === TapHelperLabelKind.Left
                            ? new THREE.Vector3(-1.5, 0, 2)
                            : new THREE.Vector3(1.5, 2.25, -1.5)
                    const point2 = new THREE.Vector3()
                    const myPin = new TwoPointPin(
                        point1,
                        point2,
                        this.camera,
                        "DC",
                        bubbleContainer,
                        container,
                        onClick
                    )
                    this.mapPins.push({
                        obj: myPin,
                        data: { ...pin, region: getRegion(mesh) },
                        onClick,
                    })
                    this.getChildAnchor(mesh)?.add(myPin)
                }
            })
    }

    connectCameraSynchronizer(cameraSynchronizer: CameraSynchronizer): void {
        this.cameraSynchronizer = cameraSynchronizer
    }

    /**
     *
     * @param {THREE.Scene} scene The parent group to contain all globe objects.
     * @param {HTMLElement} domElement The canvas element rendering the globe.
     * @param {HTMLElement} resetButton A button to reset the globe on click.
     * @param {PerformanceManager} performanceManager
     * @param {*} gpuTier
     * @param {*} settings
     */
    constructor(
        scene: THREE.Scene,
        domElement: HTMLCanvasElement,
        resetButton: HTMLElement,
        performanceManager: PerformanceManager,
        gpuTier: any,
        settings: any,
        textureInitializer: TextureInitializer,
        tutorial: LandingPageTutorial,
        camera: THREE.PerspectiveCamera
    ) {
        super()

        this.scene = scene
        this.canvas = domElement
        /**
         * The button that resets the globe on click.
         * @type {HTMLElement}
         */
        this.resetButton = resetButton
        this.gpuTier = gpuTier
        this.settings = settings

        // TODO refactor major: move the center to 0, 0, 0
        this.center = new THREE.Vector3(0, 50, 0)
        this._tempSpherical = new THREE.Spherical()
        this._tempVector3 = new THREE.Vector3()
        this._temp2Vector3 = new THREE.Vector3()

        this.lowerColor = new THREE.Color("#111")
        this.lowerEmissive = new THREE.Color("#050505")
        this.unfadeColor = new THREE.Color("#FFF")
        this.unfadeEmissive = new THREE.Color("#000000")

        domElement.addEventListener("pointerdown", (event) => {
            this.onPointerDown(event)
        })
        domElement.addEventListener("pointermove", (event) => {
            this.onPointerMove(event)
        })
        domElement.addEventListener("mouseleave", (event) => {
            this.onMouseLeave(event)
        })
        domElement.addEventListener("wheel", (event) => {
            this.onMouseWheel(event)
        })

        /**
         * State information for the globe.
         *
         * This does not represent a comprehensize globe state.
         * The state is scattered through the code.
         * My tentative plan is to slowly consolidate the globe state in this object.
         * For now, consider these read-only.
         * @type {{
         *  zoomLevel: number,
         *  aStateHasBeenClicked: boolean,
         * }}
         */
        this.state = {
            // TODO: Possibly add more zoom levels - 2 for county level, 3 for zip level, etc.
            /**
             * 0 is initial/completely zoomed out, 1 is state level
             * @type {number}
             */
            zoomLevel: 0,
            /**
             * @type {boolean}
             */
            aStateHasBeenClicked: false,
            /**
             * @type {boolean}
             */
            resetButtonShown: false,
            /**
             * @type {{holePunchedRegion: Region, closeEnough: Boolean}}
             */
            map: {
                region: null,
                closeEnough: true,
            },
        }

        this.materials = {
            holePunch: new THREE.MeshLambertMaterial({
                transparent: false,
                opacity: 0,
                blending: THREE.CustomBlending,
                blendEquation: THREE.AddEquation,
                blendSrc: THREE.OneFactor,
                blendDst: THREE.ZeroFactor,
                color: "#000",
                emissive: "#000",
            }),
        }

        // Set up reset button event listener
        this.resetButton.addEventListener("click", (event) => {
            this.reset()
            this.tutorial.state.send("RESET")
        })

        this.mouseDownBool = false
        this.pointerXOnPointerDown = 0
        this.pointerYOnPointerDown = 0
        this.pointerDragDistance = 0
        this.pointerDelta = new THREE.Vector2()
        this.pointer = new THREE.Vector2(-1000, -1000)

        /**
         * Helper to detect cases where a new load is triggered before the old one finishes.
         *
         * This is set on load. A reference copy is saved, and checked on load finish.
         * A mostRecentLoad not equaling the reference copy indicates a new superceding load.
         * @type {LoadEvent}
         */
        this.mostRecentLoad = null
        this.historyStatePushed = false

        this.group = new THREE.Group()
        this.group.position.y = 50
        scene.add(this.group)

        this.leftPanel = new LeftPanel(
            document.querySelector(".left-panel"),
            document.querySelector(".walkthrough-question-mark-container")
        )
        this.tutorial = tutorial

        // Create label renderer for floating country/state/county/zip labels
        this.labelRenderer = new CSS2DRenderer()
        this.labelRenderer.setSize(innerWidth, innerHeight)
        this.labelRenderer.domElement.style.position = "absolute"
        this.labelRenderer.domElement.style.top = "0px"
        this.labelRenderer.domElement.style.pointerEvents = "none"
        this.labelRenderer.domElement.style.zIndex = "0"
        this.labelRenderer.domElement.classList.add("floating-label-container")
        document.body.appendChild(this.labelRenderer.domElement)

        // An array of what we want to consider in our raycast
        this.RAYCAST_INTERSECTABLE = []

        // This is the position in 3D space that the cursor is currently touching/intersecting.
        // If the cursor is not interesecting anything, this will be set to (0, 0, 0)
        this.CURSOR_POS_3D = new THREE.Vector3(0, 0, 0)

        this.ANIMATIONS = new AnimationGroup()

        this.raycaster = new THREE.Raycaster()

        this.SELECTED_ZIP = ""

        // Set camera and controls
        this.base_fov = 40
        let fov = this.base_fov

        // mobile view footer height consideration
        const renderHeight = document.documentElement.clientHeight
        const aspectRatio = document.documentElement.clientWidth / renderHeight
        // For anything less-than-square, start expanding the vertical FOV
        if (aspectRatio < 1) {
            // By default, PerspectiveCamera works by specifying a fixed vertical FOV and aspect ratio.
            // Once we get to a slim enough aspect ratio, we want to specify a fixed horizontal FOV instead.
            // This calculates a vertical FOV from a fixed horizontal FOV and aspect ratio.
            fov = MathHelpers.horizontalToVerticalFOV(
                this.base_fov,
                aspectRatio
            )
        }
        this.camera = camera
        this.camera.fov = fov
        this.camera.aspect = aspectRatio
        this.camera.updateProjectionMatrix()

        // this.camera.position.set(0, 50, 170);
        scene.add(this.camera)

        this.controls = new GlobeControls(camera, domElement, this)
        this.controls.enablePan = false
        this.controls.enableDamping = true
        this.controls.target = new THREE.Vector3(0, 50, 0)
        this.controls.rotateSpeed = 0.5
        this.controls.maxDistance = 300
        this.controls.minDistance = 75
        this.controls.zoomSpeed = 1
        this.controls.extraThetaOffset = (0.1 * Math.PI) / 180

        // Hide the state list initially
        this.nav_list = document.querySelector("#state_list")
        this.nav_list.classList.add("hidden")

        // Groups of objects
        // TODO: Standardize group hierarchies and initial conditions
        this.INTERSECTED = null
        this.CLICKED = null
        this.INDIVIDUAL_COUNTY = null
        this.COUNTY_GROUP = new THREE.Group()
        this.COUNTY_GROUP.userData.mapLevel = 0
        addTweenGroup(this.COUNTY_GROUP)

        this.CITY_GROUP = new THREE.Group()
        this.group.add(this.CITY_GROUP)

        this.ZIP_GROUP = null

        this.USA = new THREE.Group()
        this.USA.userData.state = "visible"
        this.group.add(this.USA)
        addTweenGroup(this.USA)

        this.COUNTRIES = new THREE.Group()
        this.group.add(this.COUNTRIES)

        this.textureInitializer = textureInitializer

        // TODO: Merge these?
        this.globeLoader = new GlobeLoader(this.group)
        this.stateLoader = new StateLoader(this.group, this.textureInitializer)

        const shadowMapSize = (() => {
            if (this.gpuTier.isMobile === true)
                return new THREE.Vector2(2048, 2048)
            else return new THREE.Vector2(4096, 4096)
        })()

        console.log({ shadowMapSize })

        // TODO: Promisify this?
        this.globeLoader.addLights(scene, this.controls, performanceManager, {
            shadowMapSize,
        })

        // Load countries
        // this.globeLoader.addGlobe(this.COUNTRIES)

        this.WATER = new THREE.Group()

        // Load water
        const waterPromise = this.globeLoader.addWater().then((gltf) => {
            gltf.scene.traverse((obj) => {
                if (obj.name === "Sphere") {
                    this.WATER = obj.children[0]
                }
            })
        })

        this.addStateListPromise = addStateList()
        this.loadStatesPromise = this.globeLoader.addStatesV2().then((gltf) => {
            this.STATE_GROUP = gltf.scene
            this.group.add(this.STATE_GROUP)
            this.attachMapPins(LayerKind.STATES)
            this.attachMapPins(LayerKind.COUNTIES)
            this.updateIntersectable()
        })
        this.loadUSAPromise = new Promise((resolve, reject) => {
            this.loadUSAPromiseResolve = resolve
            this.loadUSAPromiseReject = reject
        })

        // Load mainland
        void this.globeLoader.addMainland().then((gltf) => {
            gltf.scene.traverse((obj) => {
                if (obj.parent === gltf.scene) {
                    this.USA.add(obj)
                }
            })
            this.USA.scale.multiplyScalar(49.5)
            this.updateIntersectable()
            this.loadUSAPromiseResolve(gltf)
        })

        // Load countries
        this.loadCountriesPromise = this.globeLoader
            .addCountries("/assets/glb/globe/front_countries.glb")
            .then((gltf) => {
                this.COUNTRIES.add(gltf.scene)
                this.updateIntersectable()
            })

        /**
         * This promise represents all the resources that need to load before the globe can be seen interacted with
         */
        const essentialsLoadedPromise = Promise.all([
            this.loadCountriesPromise,
            this.loadStatesPromise,
            waterPromise,
            this.loadUSAPromise,
        ])

        // Notify that globe is ready to be interacted with
        void essentialsLoadedPromise.then(() => {
            window.dispatchEvent(new CustomEvent("globeinteractive"))
        })

        // TODO: Move this old code somewhere more relevant
        // I think #loaded contains the bottom bar among other things
        void essentialsLoadedPromise.then(() => {
            document.getElementById("loaded")?.classList.remove("hidden")
        })

        // Load non-essential resources
        void essentialsLoadedPromise
            .then(async () => {
                return await this.globeLoader.addCountries(
                    "/assets/glb/globe/back_countries.glb"
                )
            })
            .then((gltf) => {
                const currentState = urlToHistoryState(window.location.href)
                this.COUNTRIES.add(gltf.scene)
                this.updateIntersectable()
                // TODO: Handle transition into the scene based on current globe state
                if (currentState.type !== HistoryStateType.initial) {
                    // Set all children to "gray lowered" state
                    console.log(gltf, "back countries")
                    gltf.scene.children.forEach((child) => {
                        child.scale.copy(child.userData.loweredScale)
                        child.material.color.copy(this.lowerColor)
                        child.material.emissive.set(this.lowerEmissive)
                        child.children.forEach((child) => {
                            child.material?.color.copy(this.lowerColor)
                            child.material?.emissive.set(this.lowerEmissive)
                        })
                    })
                }
            })

        this.globe_svg = document.querySelector("#globe_svg")
        this.globe_svg_g = document.querySelector("#globe_svg g")
        this.globe_svg_container = document.querySelector(
            "#globe_svg_container"
        )
        this.globe_ring = document.querySelector(".globe_ring")
        if (this.globe_svg != null) {
            this.globe_svg.style.width = `${document.documentElement.clientWidth}px`
            this.globe_svg.style.height = `${document.documentElement.clientHeight}px`
        }

        const pageUrl = new URL(window.location.href)
        const abbr = pageUrl.searchParams.get("abbr")

        /// ////////////////////////
        // Initialize ballot boxes
        /// ////////////////////////
        if (document.querySelector(".ballot-row") != null) {
            this.BALLOT_BOXES = new BallotBoxPanel(
                document.querySelector(".ballot-row"),
                abbr
            )

            const electionsOverviewDiv = document.querySelector(
                ".elections-overview"
            )
            this.BALLOT_BOXES.setElectionsOverviewDiv(electionsOverviewDiv)

            // this.BALLOT_BOXES.updateAndStore({
            //     state: url_state,
            //     abbr: abbr,
            //     county: countyName
            // });
        }

        this.pointerUpListener = (event) => {
            this.onPointerUp(event)
        }

        this.controls.addEventListener("change", () => {
            this.onCameraChange()
        })

        // Hook up self event listeners
        this.addEventListener("zoomlevelchange", (event) => {
            this.onZoomLevelChange(event)
        })

        this.dispatchFooterChangeEvent()
    }

    // dispose(): void {
    //     this.canvas.
    // }

    /**
     * Reset the globe to its initial state
     */
    reset(modifyHistory = true): void {
        // Reset URL state
        if (modifyHistory) {
            const historyState = initialHistoryState()
            updateHistory(historyState)
        }

        this._optimizationShowCountries()

        this.tutorial.state.send("RESET")

        // Reset bottom bar
        resetList()
        this.dispatchFooterChangeEvent()

        // Reset camera parameters
        this.controls.setMinDistance(75)
        this.controls.setFocusRadius(51.5)

        // Cancel all tweens
        this.scene.traverse((object) => {
            try {
                getTweenGroup(object).removeAll()
            } catch {}
        })

        this.controls.abortAnimations()

        // Reset state variables
        this.state.aStateHasBeenClicked = false

        // Reset the double load safeguard
        // As a side effect, this handles the case where reset is pressed during a load
        // The load will complete, but the assets loaded will immediately be discarded
        this.mostRecentLoad = null

        /// /////////////////////////////////////
        // Handle the currently raised state
        /// /////////////////////////////////////

        // Get the currently raised state object
        const state = this.getStateByAbbr(this.COUNTY_GROUP.userData.abbr)

        if (state !== null) {
            makeVisible(getRegion(state))
        }

        /// /////////////////////////////////////
        // End handling the currently raised state
        /// /////////////////////////////////////

        // Remove county meshes
        if (this.areCountiesLoaded()) {
            this.removeCountyGroup(this.COUNTY_GROUP)
        }

        // Remove zip meshes
        if (this.areZipsLoaded()) {
            this.INDIVIDUAL_COUNTY?.dispose(false, false)
        }

        // Move the camera to the initial orientation
        this.controls.focus(new THREE.Vector3(0, 51, 1.5))

        // Move the camera to the initial distance from the globe
        this.controls.animateTo("trackPos", 0.13, 0.9)

        // Reset everything that was gray to its initial color
        unGrayAll(this.STATE_GROUP)
        unGrayAll(this.COUNTRIES)
    }

    /**
     * @returns Canvas that the globe is rendering to
     */
    getCanvas(): HTMLCanvasElement {
        return this.canvas
    }

    getCountyByName(name: string): THREE.Mesh | undefined {
        let county
        this.COUNTY_GROUP.traverse((obj) => {
            if ("isMesh" in obj && obj.isMesh === false) return

            if (obj.userData.name === name) county = obj
        })
        return county
    }

    getLatLon(point: THREE.Vector3): { latitude: number; longitude: number } {
        this._tempVector3.copy(point)

        this.group.updateWorldMatrix(true, false)
        // point = scope.group.worldToLocal(point);
        this._tempVector3.y -= 50
        this._tempSpherical.setFromVector3(this._tempVector3)
        const lon = -90 + THREE.MathUtils.radToDeg(this._tempSpherical.theta)
        const lat = 90 - THREE.MathUtils.radToDeg(this._tempSpherical.phi)
        return {
            latitude: lat,
            longitude: lon,
        }
    }

    // Bandaid for the transition between landing page and map page
    encodeControlParams(url: URL, position: THREE.Vector3): void {
        const controls = this.controls
        const camera = this.camera
        url.searchParams.append("trackPos", controls.trackPos.toString())
        this._tempSpherical.setFromVector3(position)
        url.searchParams.append("phi", this._tempSpherical.phi.toString())
        url.searchParams.append("theta", this._tempSpherical.theta.toString())
        url.searchParams.append("x", camera.position.x.toString())
        url.searchParams.append("y", camera.position.y.toString())
        url.searchParams.append("z", camera.position.z.toString())
    }

    getCityMesh(name: string): THREE.Mesh | undefined {
        let mesh
        // Replace spaces with _
        name = name.replace(/ /g, "_")
        this.CITY_GROUP.traverse((obj) => {
            console.log(obj.name)
            if (obj.name.split("$")[0] === name) {
                mesh = obj
            }
        })
        return mesh
    }

    isUSA(obj: THREE.Object3D): boolean {
        if (obj.parent === this.USA) return true
        return false
    }

    isAnchor(obj: THREE.Object3D): boolean {
        const words = obj.name.split("_")
        const suffix = words[words.length - 1]
        return suffix !== "" && suffix.startsWith("Anchor")
    }

    getChildAnchor(obj): THREE.Object3D | undefined {
        for (let i = 0; i < obj.children.length; i++) {
            if (this.isAnchor(obj.children[i])) {
                return obj.children[i]
            }
        }
    }

    getMeshType(mesh: THREE.Mesh): any {
        return mesh.userData.type
    }

    onPointerMove(event: PointerEvent): void {
        const bounds = this.canvas.getBoundingClientRect()
        const x = event.clientX - bounds.left
        const y = event.clientY - bounds.top
        const newX = (x / document.documentElement.clientWidth) * 2 - 1
        const newY = -(y / document.documentElement.clientHeight) * 2 + 1

        if (this.mouseDownBool) {
            this.pointerDelta.x = newX - this.pointer.x
            this.pointerDelta.y = newY - this.pointer.y
            // Use lengthSq instead of length because it's faster
            this.pointerDragDistance += this.pointerDelta.lengthSq()
        }

        this.pointer.x = newX
        this.pointer.y = newY
    }

    onStateClicked(event): void {
        this.state.aStateHasBeenClicked = true
        if (this.INTERSECTED === null) {
            console.error("INTERSECTED was null")
            return
        }
        void this.loadCounties(this.INTERSECTED, true, true)
    }

    onMouseWheel(event: WheelEvent): void {
        const radius = new THREE.Vector3(0, 50, 0)
            .sub(this.CURSOR_POS_3D)
            .length()

        this._tempVector3.copy(this.CURSOR_POS_3D).sub(this.center)
        this._tempSpherical.setFromVector3(this._tempVector3)
        this._tempSpherical.makeSafe()
        const lat = globePolarToLat(this._tempSpherical.phi, 0)
        const lng = globeAzimuthalToLng(this._tempSpherical.theta)
        this.controls.setTBRadius(radius)
        this.controls._lngLat.set(lng, lat)
    }

    onPointerUp(event: PointerEvent): void {
        if (!event.isPrimary) return

        this.mouseDownBool = false

        document.removeEventListener("pointerup", this.pointerUpListener)

        /// //////////////////////////////
        // Drag / click disambiguation
        /// //////////////////////////////
        {
            // Final cursor position must be within this many pixels to be considered a "click"
            // This is so people can click and drag the map without accidentally clicking on a state
            // It's squared because calculating distance squared is faster than calculating distance
            const clickDragDistanceToleranceSquared = 0.00003

            // Short circuit if the distance is too far to be considered a click
            if (this.pointerDragDistance > clickDragDistanceToleranceSquared)
                return

            if (event.pointerType === "touch") {
                const bounds = this.canvas.getBoundingClientRect()
                const x = event.clientX - bounds.left
                const y = event.clientY - bounds.top
                this.pointer.x =
                    (x / document.documentElement.clientWidth) * 2 - 1
                this.pointer.y =
                    -(y / document.documentElement.clientHeight) * 2 + 1
                this.castMouseRay()
            }
        }
        /// //////////////////////////////
        // End Drag / click disambiguation
        /// //////////////////////////////

        this.pointer.x = -1000
        this.pointer.y = -1000

        // Stop here if we aren't mousing over anthing we can click
        if (this.INTERSECTED == null) return

        // Disambiguate what kind of mesh we've clicked on
        const type = this.getMeshType(this.INTERSECTED)
        if (this.isUSA(this.INTERSECTED)) {
            const output = new THREE.Vector3().copy(this.CURSOR_POS_3D)
            // output.y += 50;
            this.controls.focus(output)
            this.controls.trackInSlow(0.45, 1)
        } else if (type === "state") {
            this.onStateClicked(event)
        } else if (type === "county") {
            void this.loadZips(this.INTERSECTED)
        } else if (type === "individualCounty") {
            // Send map clicked event
            const mapClickEvent = new CustomEvent("mapclick", {
                detail: {
                    pointerUpEvent: event,
                },
            })
            this.dispatchEvent(mapClickEvent)
        } else if (type === "zip") {
            // Get the zip code from the mesh name
            // const zip = this.INTERSECTED.name.split("$")[0]

            // Set the active zip code, load in cities, update ballot boxes
            // this.selectZip(zip);

            const { latitude, longitude } = this.getLatLon(this.CURSOR_POS_3D)
            // Redirect to MapTiler page
            const county = this.ZIP_GROUP?.userData.fips as string
            const origin = window.location.origin
            const state = this.COUNTY_GROUP.userData.state_name as string
            window.location.href = `${origin}/pages/osm.php?lat=${latitude}&lng=${longitude}&state=${state}&county=${county}`
            // this.collapseDropdown(document.querySelector("#zip-code-container button"));
        }
    }

    onPointerDown(event: PointerEvent): void {
        let isStartingDrag = false

        if (!event.isPrimary) return
        if (event.pointerType === "touch") {
            isStartingDrag = true
            const bounds = this.canvas.getBoundingClientRect()
            const x = event.clientX - bounds.left
            const y = event.clientY - bounds.top
            this.pointer.x = (x / document.documentElement.clientWidth) * 2 - 1
            this.pointer.y =
                -(y / document.documentElement.clientHeight) * 2 + 1
            this.castMouseRay()
        } else {
            switch (event.button) {
                case 0:
                    isStartingDrag = true

                    break
                case 1:
                    break
                case 2:
                    break
            }
        }

        if (isStartingDrag) {
            this.mouseDownBool = true
            this.pointerDragDistance = 0
            this.pointerXOnPointerDown = this.pointer.x
            this.pointerYOnPointerDown = this.pointer.y

            // Set the drag surface distance
            // This improves drag precision for meshes away from the surface of the globe
        }

        // Send custom pointer down event for controls
        // TODO: handle multi touch?
        const radius = new THREE.Vector3(0, 50, 0)
            .sub(this.CURSOR_POS_3D)
            .length()

        this._tempVector3.copy(this.CURSOR_POS_3D).sub(this.center)
        this._tempSpherical.setFromVector3(this._tempVector3)
        this._tempSpherical.makeSafe()
        const lat = globePolarToLat(this._tempSpherical.phi, 0)
        const lng = globeAzimuthalToLng(this._tempSpherical.theta)
        this.controls.setTBRadius(radius)
        this.controls._lngLat.set(lng, lat)
        // const customEvent = new CustomEvent("pointerdown", {
        //     ...event,
        //     detail: { lng, lat, radius },
        // })
        // this.dispatchEvent(customEvent)

        document.addEventListener("pointerup", this.pointerUpListener)
    }

    onWindowResize(): void {
        const aspectRatio =
            document.documentElement.clientWidth /
            document.documentElement.clientHeight
        let fov = this.base_fov

        // For anything less-than-square, start expanding the vertical FOV
        if (aspectRatio < 1) {
            // By default, PerspectiveCamera works by specifying a fixed vertical FOV and aspect ratio.
            // Once we get to a slim enough aspect ratio, we want to specify a fixed horizontal FOV instead.
            // This calculates a vertical FOV from a fixed horizontal FOV and aspect ratio.
            fov = MathHelpers.horizontalToVerticalFOV(
                this.base_fov,
                aspectRatio
            )
        }
        this.camera.fov = fov
        this.camera.aspect = aspectRatio
        this.camera.updateProjectionMatrix()

        this.controls.onWindowResize()
        this.labelRenderer.setSize(innerWidth, innerHeight)

        // Send out bottom bar change event
        this.dispatchFooterChangeEvent()
    }

    onMouseLeave(event): void {
        this.pointer.x = -1000
        this.pointer.y = -1000
    }

    /**
     * "Lowers" a region like a state or county.
     * Used to lower regions that were previously raised.
     *
     * TODO: Refactor this to be part of region code?
     */
    lower(obj: THREE.Mesh, lowerDelay: number, grayDelay: number): void {
        obj.userData.state = "lowered"

        // Stop all previous tweens on this object
        getTweenGroup(obj).removeAll()

        const region = getRegion(obj)

        // Lower this object
        new Tween(obj.scale, getTweenGroup(obj))
            .to(obj.userData.loweredScale, 500)
            .delay(lowerDelay * 1000)
            .onComplete(() => {
                if (region.face.userData.type === "county") {
                    region.bottomBorder.visible = false
                }
            })
            .start()

        // Turn all the children gray
        if (region instanceof MapRegion) {
            obj.userData.active = false
            region.deactivate()
            this.changeMapState({ region: null })
            this.jumpToGray(obj)
            this.jumpToGray(
                obj.children.find(
                    (child) => child instanceof THREE.Mesh && isTopBorder(child)
                ) as THREE.Mesh
            )
        } else {
            obj.traverse((child) => {
                if (!("isMesh" in child) || child.isMesh === false) return

                new Tween(child, getTweenGroup(obj))
                    .to(
                        {
                            material: {
                                color: this.lowerColor,
                                emissive: this.lowerEmissive,
                            },
                            userData: {
                                inactiveColor: this.lowerColor,
                                inactiveEmissive: this.lowerEmissive,
                            },
                        },
                        500
                    )
                    .easing(Easing.Quadratic.Out)
                    .delay(grayDelay * 1000)
                    .start()
            })
        }
    }

    restoreAndLower(object: THREE.Mesh): void {
        makeVisible(getRegion(object))
        this.lower(object, 0, 0)
    }

    /**
     * Raises a region, for example when a user selects the region, or when a reset happens.
     * @param object
     */
    async raise(object: THREE.Mesh): Promise<void> {
        this.dispatchEvent(new CustomEvent("regionraise", { detail: object }))

        // if(object.region) {
        //     // Make mesh faces transparent
        //     const mat = new THREE.MeshStandardMaterial({
        //         transparent: true,
        //         opacity: 0,
        //         blending: THREE.CustomBlending,
        //         blendEquation: THREE.AddEquation,
        //         blendSrc: THREE.OneFactor,
        //         blendDst: THREE.ZeroFactor
        //     })

        //     object.region.face.material = mat
        //     console.log('REGION!')
        // }

        getRegion(object).bottomBorder.visible = true

        getTweenGroup(object)?.removeAll()
        await new Promise<void>((resolve, reject) => {
            new Tween(object.scale, getTweenGroup(object))
                .to(object.userData.clickedScale, 500)
                .easing(Easing.Quadratic.Out)
                .start()
                .onComplete(() => {
                    console.log("tween complete")
                    resolve()
                    try {
                        const region = getRegion(object)
                        if (!("isMapRegion" in region)) return
                        this.changeMapState({ region })
                    } catch (err) {
                        // No region
                    }
                })
                .onStop(() => {
                    console.log("tween stop")
                    resolve()
                })
        })
    }

    animateLowerInWave(
        group: THREE.Object3D,
        center: THREE.Vector3,
        doNotAnimateMe?: THREE.Object3D,
        speed: number = 0.5
    ): void {
        for (let i = 0; i < group.children.length; i++) {
            const obj = group.children[i]

            if (
                doNotAnimateMe != null &&
                doNotAnimateMe !== undefined &&
                obj.name === doNotAnimateMe.name
            )
                continue

            const anchor = this.getChildAnchor(obj)

            if (anchor === undefined) {
                throw new Error("Object did not have anchor")
            }

            const delay = anchor.position.angleTo(center) * (1 / speed)

            if (obj instanceof THREE.Mesh) {
                this.lower(obj, delay + 0.5, delay)
            }
        }
    }

    lowerStatesAndCountries(): void {
        // Handle the case where states are "lowered" before they're actually raised
        if (this.state.zoomLevel === 0) {
            this.STATE_GROUP.children
                .map((state) => getRegion(state))
                .forEach((region) => {
                    region.face.scale.copy(region.face.userData.randomScale)
                })
        }
        if (!this.areCountiesLoaded()) {
            const anchor = this.getChildAnchor(this.CLICKED)
            if (anchor === undefined) {
                throw new Error("Object had no anchor")
            }
            void this.loadStatesPromise.then(() => {
                this.animateLowerInWave(
                    this.STATE_GROUP,
                    anchor.position,
                    this.CLICKED === null ? undefined : this.CLICKED
                )
            })

            void this.loadCountriesPromise.then(() => {
                // for (var i = 0; i < this.COUNTRIES.children[0].children.length; i++) {
                //     const country = this.COUNTRIES.children[0].children[i];
                //     // Special case for russia to make sure it doesnt clip into water
                //     if (country.name == "Russian_Federation")
                //         country.userData.loweredScale.set(.01022, .01022, .01022);

                // }

                this.animateLowerInWave(
                    this.COUNTRIES.children[0],
                    anchor.position
                )
            })

            // if(this.areCountriesLoaded()) {
            //     for (var i = 0; i < this.COUNTRIES.children[0].children.length; i++) {
            //         const country = this.COUNTRIES.children[0].children[i];
            //         // Special case for russia to make sure it doesnt clip into water
            //         if (country.name == "Russian_Federation")
            //             country.userData.loweredScale.set(.01022, .01022, .01022);

            //     }

            //     this.animateLowerInWave(this.COUNTRIES.children[0], this.getChildAnchor(this.CLICKED).position, null);
            // }
        }
    }

    lowerAnythingRaised(): void {
        // Initial "gray wave" animation
        this.lowerStatesAndCountries()
        this.lowerZips()

        // Handle raised counties
        if (this.areCountiesLoaded()) {
            const state = this.getStateByAbbr(this.COUNTY_GROUP.userData.abbr)
            if (state === null) {
                console.error("state is null")
                return
            }
            if (!(state instanceof THREE.Mesh)) {
                console.error("state is not a mesh")
                return
            }
            this.ANIMATIONS.killAll(state, state.material, state.userData)
            state.traverse((obj) => {
                this.ANIMATIONS.killAll(obj, obj.userData)
                if (obj instanceof THREE.Mesh) {
                    this.ANIMATIONS.killAll(obj.material)
                }
            })
            this.restoreAndLower(state)
            this.removeCountyGroup(this.COUNTY_GROUP)
        }
    }

    lowerZips(): void {
        if (!this.areZipsLoaded()) {
            if (this.CLICKED?.userData.type === "county") {
                this.restoreAndLower(this.CLICKED)
            }
            return
        }

        const county = this.CLICKED
        if (county !== null) {
            this.ANIMATIONS.killAll(county, county.material, county.userData)
            county.traverse((obj) => {
                if (!(obj instanceof THREE.Mesh)) return
                this.ANIMATIONS.killAll(obj, obj.material, obj.userData)
            })
            this.restoreAndLower(county)
        }
        this.INDIVIDUAL_COUNTY?.dispose(false, false)
        this.ZIP_GROUP = null
    }

    fadeOut(obj: THREE.Object3D): void {
        // if (!obj.isMesh)
        //     return;

        // const mesh = obj;
        // mesh.castShadow = false;
        // mesh.receiveShadow = false;

        // // Bottom border material is shared, so we swap material during fade to avoid affecting the other meshes
        // mesh.userData.oldMaterial = mesh.material;
        // mesh.material = mesh.material.clone();
        // if (mesh.name.endsWith("_BottomBorder")) {
        //     // Add radial gradient shader snippet to the bottom border material shader
        //     mesh.material.onBeforeCompile = function (shader, renderer) {
        //         injectFakeAO(shader, renderer);
        //     };
        // }

        // // mesh.material.transparent = true;

        // const animation = new Animation(mesh.material, "opacity", 0, 0.1, 0);
        // animation.promise.then(() => {
        //     const oldMaterial = mesh.material;
        //     mesh.material = mesh.userData.oldMaterial;
        //     oldMaterial.dispose();

        //     mesh.visible = false;
        //     this.updateIntersectable();
        // });
        // this.ANIMATIONS.add(animation);
        obj.visible = false
    }

    /**
     * Handles functionality relating to the display of map textures on MapRegions
     * @param param0
     * @returns
     */
    changeMapState({
        region,
        closeEnough,
    }: {
        region?: any
        closeEnough?: any
    }): void {
        if (region !== undefined && closeEnough !== undefined) {
            console.error(
                "changeMapState can't change multiple state values simultaneously but tried",
                region,
                closeEnough
            )
            return
        }
        const regionChanged =
            region !== undefined && region !== this.state.map.region
        const closeEnoughChanged =
            closeEnough !== undefined &&
            closeEnough !== this.state.map.closeEnough

        if (closeEnoughChanged) {
            if (closeEnough === false) {
                // Camera went too far to show map
                if (this.state.map.region !== null) {
                    this.state.map.region.unHolePunch()
                    this.dispatchEvent(new CustomEvent("mapleave"))
                }
            } else if (closeEnough === true) {
                // Camera came close enough to show map
                if (this.state.map.region != null) {
                    this.dispatchEvent(new CustomEvent("mapenter"))
                    // Wait one frame before hole punching to give the map time to update
                    requestAnimationFrame(() => {
                        this.state.map.region?.holePunch()
                    })
                }
            }
            this.state.map.closeEnough = closeEnough
            return
        }

        if (regionChanged) {
            if (this.state.map.closeEnough) {
                if (region === null) {
                    // Old region stopped displaying map
                    this.state.map.region?.unHolePunch()
                    this.dispatchEvent(new CustomEvent("mapleave"))
                } else {
                    // New region started displaying map
                    region.holePunch()
                    this.dispatchEvent(new CustomEvent("mapenter"))
                }
            }
            this.state.map.region = region
        }
    }

    // TODO: Refactor into a loader library?
    async safeLoad<T>(promise: Promise<T>): Promise<T> {
        // Set safety guard
        this.mostRecentLoad = new LoadEvent(this.CLICKED)
        const currentLoad = this.mostRecentLoad

        // Do load
        const resource = await promise

        // Check safety guard
        if (!(this.mostRecentLoad === currentLoad)) {
            if (
                resource instanceof Object &&
                "scene" in resource &&
                resource.scene instanceof THREE.Object3D
            )
                this.removeCountyGroup(resource.scene)
            throw new Error(
                "A different region was clicked while this one was loading"
            )
        }

        return resource
    }

    // TODO: Refactor into a loader library?
    isDoubleLoad(obj: any): boolean {
        return this.mostRecentLoad?.object === obj
    }

    loadCountiesWithRaisePromise(
        state: string | THREE.Mesh,
        zoom: boolean = true,
        options: LoadCountiesOptions
    ): [Promise<null | GLTF>, Promise<void>] {
        const { modifyHistory = true, modifyTutorial = true, signal } = options
        let stateMesh: THREE.Mesh

        if (typeof state === "string") {
            stateMesh = this.getStateByName(state)
        } else {
            stateMesh = state
        }

        // if (this.isDoubleLoad(stateMesh)) return null

        // Special case for DC, which only has one county. Don't add county history step.
        if (stateMesh.userData.fips === "11") {
            // Skip history step
        } else {
            if (modifyHistory) {
                const historyState = globeHistoryState({
                    fips: stateMesh.userData.fips,
                })
                updateHistory(historyState)
            }
        }

        if (modifyTutorial) {
            this.tutorial.state.send("LOAD_COUNTIES")
        }
        this.dispatchEvent(new CustomEvent("entercounties"))
        this.changeMapState({ region: null })

        /// ////////////////////////////
        // Update bottom menu
        /// ////////////////////////////

        // Switch to state list
        // switchToStateList()

        // // Unhighlight the old button
        // {
        //     const button = document.querySelector(
        //         ".state_list ul li.highlighted"
        //     )
        //     // No need for error handling; this is expected to be null the first time
        //     button?.classList.remove("highlighted")
        // }

        // // Select and scroll to new button
        // const button = document.querySelector(
        //     `.state_list ul li#${stateMesh.userData.abbr as string}`
        // )
        // if (button === null) {
        //     // Handle error - highly unexpected
        //     console.error(
        //         stateMesh.userData.abbr,
        //         "Could not find state button"
        //     )
        // } else {
        //     button.classList.add("highlighted")
        //     scrollBottomBarToHighlighted("state_list")
        // }

        this._optimizationHideCountries()

        // TODO: #abort Abort
        const countyListPromise = switchToCountyList(
            stateMesh.userData.abbr,
            ""
        ).then((firstLoad) => {
            // Show state header (which is the state list for some reason, ew)
            const list = document.querySelector(".state_list")
            if (list === null) {
                // Handle error - highly unexpected
                console.error("State list element not found")
            } else {
                list.classList.remove("hidden")
                // this.dispatchFooterChangeEvent()
            }
            // TODO: #abort Pass in abort signal
            this.dispatchFooterChangeEvent()
            return firstLoad
        })

        /// //////////////////////
        // Zoom camera
        /// //////////////////////
        if (zoom) {
            this.controls.setMinDistance(53.2)
            this.controls.focusMesh(stateMesh, 1, true)
        }

        if (
            this.areCountiesLoaded() &&
            stateMesh.userData.fips === this.COUNTY_GROUP.userData.fips
        ) {
            // Go back to state level being active
            this.lowerZips()

            // Unset region. This will indicate that the map is no longer visible.
            this.changeMapState({ region: null })

            // Update tutorial state. This has the side effect of sliding back the left panel
            this.INDIVIDUAL_COUNTY?.dispose(false, false)
            this.unFadeToGray(this.COUNTY_GROUP)
            this.updateIntersectable()
            return [Promise.resolve(null), Promise.resolve()]
        }

        this.CLICKED = stateMesh

        // Play animations
        this.lowerAnythingRaised()

        /// /////////////////////////////////////////////////////////////////
        // Special case - don't load counties in screenshot mode
        /// /////////////////////////////////////////////////////////////////
        if (
            this.ballot_screenshot_mode !== null &&
            this.ballot_screenshot_mode !== ""
        ) {
            setTimeout(() => {
                if (this.CLICKED === null) {
                    console.error("CLICKED is null")
                    return
                }
                this.CLICKED.scale.copy(this.CLICKED.userData.clickedScale)
                if ("color" in this.CLICKED.material) {
                    const color = this.CLICKED.material.color as THREE.Color
                    color.set("rgb(60, 52, 43)")
                } else {
                    console.error("CLICKED.material has no color")
                }
            }, 1000)
            return [Promise.resolve(null), Promise.resolve()]
        }

        const raisePromise = this.raise(stateMesh)

        /// //////////////////////
        // Load in new counties
        /// //////////////////////
        let gltfPromise = this.safeLoad(
            this.stateLoader.addCounties(this.CLICKED.userData.abbr, {
                signal: options.signal,
            })
        )

        gltfPromise = gltfPromise
            .then((gltf) => {
                if (this.CLICKED === null) {
                    throw new Error("CLICKED is null")
                }
                if (
                    options?.signal?.aborted !== null &&
                    options?.signal?.aborted !== undefined &&
                    options.signal.aborted
                ) {
                    console.warn("Aborted")
                    throw new Error(options.signal.reason)
                }
                // Put the new counties in the scene
                this.COUNTY_GROUP = gltf.scene
                addTweenGroup(this.COUNTY_GROUP)
                this.COUNTY_GROUP.userData.state_name =
                    this.CLICKED.userData.name.replace(/_/g, " ")
                this.COUNTY_GROUP.userData.abbr = this.CLICKED.userData.abbr
                this.COUNTY_GROUP.userData.fips = this.CLICKED.userData.fips
                this.group.add(this.COUNTY_GROUP)
                this.updateIntersectable()

                void transferScale(this.CLICKED, gltf.scene)

                // Fade out state meshes
                this.CLICKED.traverse((obj) => {
                    this.fadeOut(obj)
                })

                // Special case for DC, which only has one county. Immediately load zips.
                if (stateMesh.userData.fips === "11") {
                    // Wait for the "county" to raise before loading zips
                    // TODO: Fix the root cause preventing immediate loading
                    void raisePromise.then(async () => {
                        // TODO: Add signal
                        await this.loadZips(
                            gltf.scene.children[0] as THREE.Mesh,
                            { modifyHistory, signal }
                        )
                    })
                }
                return gltf
            })
            .catch((err) => {
                throw err
            })

        // Set up county list callbacks when both county list and gltf are loaded
        Promise.all([gltfPromise, countyListPromise])
            .then(([_, firstLoad]) => {
                signal?.throwIfAborted()
                if (!firstLoad) return

                // Set up county list callbacks
                document
                    .querySelectorAll(".county_list li")
                    .forEach((element) => {
                        const a = element.querySelector("span")
                        if (a === null) {
                            console.error(
                                "County list <li> did not have <span> child"
                            )
                            return
                        }
                        const fips = element.getAttribute("fips")
                        if (fips === null) {
                            console.error(
                                "County list <li> did not have fips attribute"
                            )
                            return
                        }
                        const mesh = this.getMeshByFips(fips)
                        if (mesh === null) {
                            console.error(
                                `No mesh corresponding to fips code ${fips} found`
                            )
                            return
                        }

                        a.addEventListener("mouseover", (event) => {
                            this.highlight(mesh)
                        })

                        a.addEventListener("mouseout", (event) => {
                            this.unhighlight(mesh)
                        })

                        a.addEventListener("click", (event) => {
                            void this.loadZips(mesh)
                        })
                    })
            })
            .catch((err) => {
                console.warn(err)
            })

        return [gltfPromise, raisePromise]
    }

    async loadCounties(
        state: string | THREE.Mesh,
        zoom: boolean = true,
        modifyHistoryOrOptions?: boolean | LoadCountiesOptions
    ): Promise<null | GLTF> {
        let modifyHistory = true
        let modifyTutorial = true
        let signal: AbortSignal | undefined
        if (modifyHistoryOrOptions === undefined) {
            modifyHistory = true
        } else if (typeof modifyHistoryOrOptions === "boolean") {
            modifyHistory = modifyHistoryOrOptions
        } else {
            ;({
                modifyHistory = true,
                modifyTutorial = true,
                signal,
            } = modifyHistoryOrOptions)
        }
        const [gltfPromise] = this.loadCountiesWithRaisePromise(state, zoom, {
            modifyHistory,
            modifyTutorial,
            signal,
        })
        return await gltfPromise
    }

    getMeshByFips(fips: string): THREE.Mesh | null {
        let mesh: THREE.Mesh | null = null
        if (fips.length === 5) {
            this.COUNTY_GROUP.traverse((obj) => {
                if (!(obj instanceof THREE.Mesh)) return

                if (obj.name === fips) mesh = obj
            })
        }
        return mesh
    }

    isRegionSpecifiedInUrl(): boolean {
        // const url = new URL(window.location);
        // return url.searchParams.has("fips")
        //     || url.searchParams.has("state");
        return true
    }

    fadeToGray(group: THREE.Object3D): void {
        if (!isMapGroup(group)) {
            console.warn("fadeToGray on non-map groups not implemented")
            return
        }

        group.userData.materials.inactive.forEach((material) => {
            new Tween(material, getTweenGroup(group))
                .to(
                    {
                        color: this.lowerColor,
                        emissive: this.lowerEmissive,
                    },
                    500
                )
                .easing(Easing.Quadratic.Out)
                .start()
        })

        group.children
            .filter(isMapMesh)
            .map((obj) => obj.children.find(isTopBorder))
            .forEach((topBorder) => {
                if (topBorder === undefined) {
                    console.error("Map mesh did not have top border")
                    return
                }
                if (!(topBorder instanceof THREE.Mesh)) {
                    console.error("Top border was not a mesh")
                    return
                }
                new Tween(topBorder.material, getTweenGroup(group))
                    .to(
                        {
                            color: this.lowerColor,
                            emissive: this.lowerEmissive,
                        },
                        500
                    )
                    .easing(Easing.Quadratic.Out)
                    .start()
            })

        group.children
            .filter((obj) => {
                try {
                    return getRegion(obj) instanceof MapRegion
                } catch {
                    return false
                }
            })
            .forEach((mesh) => {
                ;(getRegion(mesh) as MapRegion).deactivate()
            })
    }

    unFadeToGray(group: THREE.Object3D): void {
        if (!isMapGroup(group)) {
            console.warn("unFadeToGray on non-map groups not implemented")
            return
        }

        group.userData.materials.inactive.forEach((material) => {
            new Tween(material, getTweenGroup(group))
                .to(
                    {
                        color: this.unfadeColor,
                        emissive: this.unfadeEmissive,
                    },
                    500
                )
                .easing(Easing.Quadratic.Out)
                .start()
        })

        group.children
            .filter(isMapMesh)
            .map((obj) => obj.children.find(isTopBorder))
            .forEach((topBorder) => {
                if (topBorder === undefined) {
                    console.error("Map mesh did not have top border")
                    return
                }
                if (!(topBorder instanceof THREE.Mesh)) {
                    console.error("Top border was not a mesh")
                    return
                }
                new Tween(topBorder.material, getTweenGroup(group))
                    .to(
                        {
                            color: this.unfadeColor,
                            emissive: this.unfadeEmissive,
                        },
                        500
                    )
                    .easing(Easing.Quadratic.Out)
                    .start()
            })

        group.children
            .filter((obj) => {
                try {
                    return getRegion(obj) instanceof MapRegion
                } catch {
                    return false
                }
            })
            .forEach((mesh) => {
                ;(getRegion(mesh) as MapRegion).activate()
            })
    }

    jumpToGray(mesh: THREE.Mesh): void {
        if (mesh.parent === null) {
            console.warn("Mesh had no parent")
            return
        }

        if (isMapMesh(mesh)) {
            mesh.material =
                mesh.parent.userData.materials.inactive[
                    mesh.parent.userData.mapLevel
                ]
        } else {
            if (!("color" in mesh.material)) {
                console.warn("Mesh material did not have color")
                return
            }
            const color = mesh.material.color as THREE.Color
            color.copy(this.lowerColor)
            if (!("emissive" in mesh.material)) {
                // This is the case for basic materials
                return
            }
            const emissive = mesh.material.emissive as THREE.Color
            emissive.copy(this.lowerEmissive)
        }
    }

    async loadZips(
        countyMesh: THREE.Mesh,
        {
            modifyHistory = true,
            modifyTutorial = true,
            signal,
        }: LoadZipsOptions = {}
    ): Promise<void> {
        this.dispatchEvent(
            new CustomEvent("regionload", {
                detail: {
                    type: "zip",
                    id: countyMesh.userData.fips,
                    name: countyMesh.userData.name,
                    county: countyMesh.userData.fips,
                    state: this.COUNTY_GROUP.userData.state_name,
                },
            })
        )
        // if (this.isDoubleLoad(countyMesh))
        //     return null;

        if (modifyHistory) {
            const historyState = globeHistoryState({
                fips: countyMesh.userData.fips,
            })
            updateHistory(historyState)
        }

        // TODO: #abort Abort
        this.lowerZips()

        // Highlight county on bottom bar
        const prevSelected = document.querySelector(".county_list .selected")
        prevSelected?.classList.remove("selected")
        const ele = document.querySelector(
            `li[fips='${countyMesh.userData.fips as string}']`
        )
        if (ele != null) {
            ele.classList.add("selected")
            scrollBottomBarToHighlighted("county_list")
        } else {
            console.warn(
                `Mesh with fips ${
                    countyMesh.userData.fips as string
                } and name ${
                    countyMesh.userData.name as string
                } has no corresponding bottom bar entry`
            )
        }

        this.tutorial.state.send("LOAD_ZIPS")
        // TODO: #abort Add abort signal to CustomEvent
        this.dispatchEvent(new CustomEvent("loadzips"))
        this.CLICKED = countyMesh

        if (!this.areZipsLoaded()) {
            // TODO: #abort Pass in abort signal
            this.fadeToGray(this.COUNTY_GROUP)
        }

        // TODO: #abort Pass in abort signal
        const raisePromise = this.raise(countyMesh)

        this.controls.setMinDistance(53.15)
        this.controls.focusMesh(countyMesh, 1, true, 53.2, 1.5)

        const promise = this.safeLoad(
            this.stateLoader.addIndividualCounty(countyMesh.userData.fips, {
                signal,
            })
        )

        // TODO: Fix this for map geojson
        // this.leftPanel.setCounty(countyMesh.userData.name, promise, this);

        let mesh: THREE.Mesh
        try {
            mesh = await promise
        } catch (error) {
            console.warn(error)
            return
        }
        signal?.throwIfAborted()

        await raisePromise
        signal?.throwIfAborted()

        const region = getRegion(mesh)
        getRegion(this.CLICKED).hide()
        region.face.scale.copy(
            new THREE.Vector3()
                .copy(this.CLICKED.scale)
                .multiplyScalar(this.COUNTY_GROUP.scale.x)
        )
        region.face.material = this.materials.holePunch
        this.group.add(region.face)

        this.INDIVIDUAL_COUNTY = region
        this.updateIntersectable()

        // // Make mesh faces transparent
        // const mat = new THREE.MeshStandardMaterial({
        //     transparent: true,
        //     opacity: 0,
        //     blending: THREE.CustomBlending,
        //     blendEquation: THREE.AddEquation,
        //     blendSrc: THREE.OneFactor,
        //     blendDst: THREE.ZeroFactor
        // })

        // this.ZIP_GROUP.region.faces.forEach(face => {
        //     face.material = mat
        // })

        // return gltf;
    }

    /**
     * Highlights a mesh, for example when mousing over it.
     */
    highlight(mesh: THREE.Mesh): void {
        try {
            getRegion(mesh).highlight(this.settings.hoverColor.color)
        } catch {
            // TODO: Check if this code is still needed
            if ("color" in mesh.material) {
                const color = mesh.material.color as THREE.Color
                color.set(this.settings.hoverColor.color)
            }
        }

        // Show the label
        const intersectedLabel = getRegion(mesh)?.label
        if (intersectedLabel != null) {
            intersectedLabel.visible = true
            this.labelRenderer.render(
                intersectedLabel as any as THREE.Scene,
                this.camera
            )
        }
    }

    unhighlight(mesh: THREE.Mesh): void {
        try {
            getRegion(mesh).unhighlight()
        } catch {
            // TODO: Check if this code is still needed
            if ("color" in mesh.material) {
                const color = mesh.material.color as THREE.Color
                color.copy(mesh.userData.inactiveColor)
            }
        }

        const intersectedLabel = getRegion(mesh)?.label
        if (intersectedLabel != null) {
            intersectedLabel.visible = false
            this.labelRenderer.render(
                intersectedLabel as any as THREE.Scene,
                this.camera
            )
        }
    }

    castMouseRay(): void {
        // Position the raycaster
        this.raycaster.setFromCamera(this.pointer, this.camera)

        // Do the raycast
        const intersections = this.raycaster.intersectObjects(
            this.RAYCAST_INTERSECTABLE,
            false
        )

        let object: THREE.Object3D | undefined
        if (intersections.length !== 0) {
            // Convenience variable for later
            object = intersections[0].object

            // Update CURSOR_POS_3D
            this.CURSOR_POS_3D.copy(intersections[0].point)
        } else {
            // Clear CURSOR_POS_3D
            this.CURSOR_POS_3D.set(0, 0, 0)
        }

        // Short circuit if we're still intersecting the same old object
        if (this.INTERSECTED === object) return

        if (this.INTERSECTED != null) {
            // Un-highlight the previously highlighted mesh
            this.unhighlight(this.INTERSECTED)
        }

        // Short circuit if we intersected water
        if (object === this.WATER) {
            this.INTERSECTED = null
            return
        }

        // Store intersected object as a global variable for events to interact with
        // This line also unsets INTERSECTED if we're not intersecting anything
        if (object instanceof THREE.Mesh) {
            this.INTERSECTED = object != null ? object : null
            this.highlight(object)
        } else {
            this.INTERSECTED = null
        }
    }

    /**
     * Main render entrypoint
     * @param elapsed
     */
    render(renderer: THREE.WebGLRenderer): void {
        renderer.render(this.scene, this.camera)
        // Render region labels
        if (
            this.INTERSECTED != null
            // getRegion(this.INTERSECTED)?.id !== "11" // Exclude DC
        ) {
            if (
                this.highlightedLabel !== null &&
                this.highlightedLabel !== undefined
            ) {
                this.labelRenderer.render(
                    this.highlightedLabel as any,
                    this.camera
                )
            }
        }

        // Render map pins
        for (const pin of this.mapPins) {
            const region = pin.data.region

            // Get distance between camera and anchor
            region.anchor.getWorldPosition(this._tempVector3)
            this.camera.getWorldPosition(this._temp2Vector3)
            this._tempVector3.sub(this._temp2Vector3)
            const distance = this._tempVector3.length()

            // Get the fips of the currently raised region
            const raisedAbbr = this.currentLayerGroup.userData.abbr

            if (
                // angularDistance < pin.data.angularThreshold &&
                distance < pin.data.distanceThreshold.far &&
                distance >= pin.data.distanceThreshold.near &&
                (pin.data.onlyWhileRaised === undefined ||
                    pin.data.onlyWhileRaised === raisedAbbr) &&
                (pin.data.exclude === undefined ||
                    !pin.data.exclude.includes(raisedAbbr)) &&
                pin.data.layer === this.currentLayerKind
            ) {
                if (pin.data.pinKind === TapHelperLabelKind.Vertical) {
                    const obj = pin.obj as CSS2DObject
                    obj.visible = true
                    this.labelRenderer.render(obj as any, this.camera)
                } else if (
                    pin.data.pinKind === TapHelperLabelKind.Left ||
                    pin.data.pinKind === TapHelperLabelKind.Right
                ) {
                    const obj = pin.obj as TwoPointPin
                    obj.visible = true
                    obj.label.visible = true
                    obj.line.visible = true
                    this.labelRenderer.render(obj as any, this.camera)
                    obj.label.getWorldPosition(this._tempVector3)
                    obj.line.getWorldPosition(this._temp2Vector3)
                    obj.updateLine(
                        this._tempVector3,
                        this._temp2Vector3,
                        this.camera
                    )
                }
            } else {
                if (pin.data.pinKind === TapHelperLabelKind.Vertical) {
                    const obj = pin.obj as CSS2DObject
                    obj.visible = false
                    this.labelRenderer.render(obj as any, this.camera)
                } else if (
                    pin.data.pinKind === TapHelperLabelKind.Left ||
                    pin.data.pinKind === TapHelperLabelKind.Right
                ) {
                    const obj = pin.obj as TwoPointPin
                    obj.visible = false
                    obj.label.visible = false
                    obj.line.visible = false
                    this.labelRenderer.render(pin.obj as any, this.camera)
                }
            }
        }
    }

    fadeOutRing(): void {
        if (this.globe_ring === null) {
            console.warn("globe_ring was null")
            return
        }
        this.globe_ring.classList.remove("fade-in")
        this.globe_ring.classList.add("fade-out")
    }

    fadeInRing(): void {
        if (this.globe_ring === null) {
            console.warn("globe_ring was null")
            return
        }
        this.globe_ring.classList.remove("fade-out")
        this.globe_ring.classList.add("fade-in")
    }

    fadeOutTapHelpers(): void {
        const eles =
            document.querySelectorAll<HTMLDivElement>(".tab-helper-root")
        eles.forEach((ele) => {
            // ele.classList.remove("fade-in")
            ele.classList.add("fade-out")
            ele.style.pointerEvents = "none"
        })
    }

    fadeInTapHelpers(): void {
        const eles =
            document.querySelectorAll<HTMLDivElement>(".tab-helper-root")
        eles.forEach((ele) => {
            // ele.classList.add("fade-in")
            ele.classList.remove("fade-out")
            ele.style.pointerEvents = ""
        })
    }

    updateTweens(): void {
        this.ANIMATIONS.update()

        /* Update tweens */
        TweenUpdate()
        getTweenGroup(this.USA).update()
        this.STATE_GROUP?.children.forEach((state) => {
            getTweenGroup(state).update()
        })
        this.COUNTRIES.children[0]?.children.forEach((country) => {
            getTweenGroup(country).update()
        })
        try {
            getTweenGroup(this.COUNTY_GROUP).update()
        } catch {}
        this.COUNTY_GROUP?.children.forEach((county) => {
            if (!isMapMesh(county)) return
            getTweenGroup(county).update()
        })
        this.ZIP_GROUP?.children.forEach((zip) => {
            getTweenGroup(zip).update()
        })
    }

    /**
     *
     * @param elapsed Do all animation
     */
    animate(elapsed: number): void {
        if (this.gpuTier.isMobile === false)
            // Find if the mouse is intersecting with anything
            this.castMouseRay()

        this.controls.update()
        this.updateTweens()

        /* Rotate the ring around the globe */
        if (this.globe_ring != null) {
            let dist = this.controls.getDistance()
            let scale: number

            if (dist >= 170) {
                scale = 170 / dist
            } else {
                dist = Math.pow(dist, 2)
                scale = 28900 / dist
            }

            // One rotation every 8 seconds
            const angle = ((elapsed * 360) / 8) % 360
            this.globe_ring.style.setProperty(
                "transform",
                `translate(-50%,-50%) scale(${scale}) rotate(${angle}deg)`
            )
        }

        /* Do map texture swaps */
        const dist = this.controls.getDistance()

        // TODO OPTIMIZATION-CPU: Calculate this once for each county group
        let sqrt = Math.sqrt(
            this.COUNTY_GROUP.children[0]?.userData.groupMapArea
        )
        if (isNaN(sqrt)) sqrt = 0
        const distCutoff = 54 + sqrt / 4

        if (dist < distCutoff && this.COUNTY_GROUP.userData.mapLevel === 0) {
            this.COUNTY_GROUP.userData.mapLevel = 1
            const materials = this.COUNTY_GROUP.userData.materials as
                | { active: THREE.Material[] }
                | undefined
            if (
                materials?.active[this.COUNTY_GROUP.userData.mapLevel] != null
            ) {
                this.COUNTY_GROUP.children.forEach((obj) => {
                    if (!(obj instanceof THREE.Mesh)) return
                    if (!isMapMesh(obj)) return
                    try {
                        const region = getRegion(obj)
                        if (region instanceof MapRegion) {
                            region.setMapLevel(
                                this.COUNTY_GROUP.userData.mapLevel
                            )
                        } else {
                            console.error("County child was not a map region")
                        }
                    } catch {}
                })
            }
        } else if (
            dist > distCutoff + 1 &&
            this.COUNTY_GROUP.userData.mapLevel === 1
        ) {
            this.COUNTY_GROUP.userData.mapLevel = 0
            const materials = this.COUNTY_GROUP.userData.materials as
                | { active: THREE.Material[] }
                | undefined
            if (
                materials?.active[this.COUNTY_GROUP.userData.mapLevel] != null
            ) {
                this.COUNTY_GROUP.children.forEach((obj) => {
                    if (!(obj instanceof THREE.Mesh)) return
                    if (!isMapMesh(obj)) return
                    try {
                        const region = getRegion(obj)
                        if (region instanceof MapRegion) {
                            region.setMapLevel(
                                this.COUNTY_GROUP.userData.mapLevel
                            )
                        } else {
                            console.error("County child was not a map region")
                        }
                    } catch {}
                })
            }
        }

        /* Auto-hide individual county region (keywords: auto lower, autolower) */
        if (
            !this.controls.isFocusing &&
            this.INDIVIDUAL_COUNTY != null &&
            !this.INDIVIDUAL_COUNTY.disposed &&
            this.INDIVIDUAL_COUNTY.id !== "11001" // Exception for District of Columbia
        ) {
            // console.log(this.INDIVIDUAL_COUNTY.id)
            const threshold = 0.25 // degrees latitude/longitude
            const lat =
                90 -
                (this.controls.getPolarAngle() +
                    this.controls.getExtraThetaOffset()) *
                    MathHelpers.RAD2DEG

            const lon =
                -90 + this.controls.getAzimuthalAngle() * MathHelpers.RAD2DEG

            const { minx, miny, maxx, maxy } =
                this.INDIVIDUAL_COUNTY.face.userData
            if (
                !this.isInBounds(lon, minx, maxx, threshold) ||
                !this.isInBounds(lat, miny, maxy, threshold)
            ) {
                // Go back to state level being active
                this.lowerZips()

                // Unset region. This will indicate that the map is no longer visible.
                this.changeMapState({ region: null })

                // Update tutorial state. This has the side effect of sliding back the left panel
                this.tutorial.state.send("LOAD_COUNTIES")
                this.INDIVIDUAL_COUNTY.dispose(false, false)
                this.unFadeToGray(this.COUNTY_GROUP)
                this.updateIntersectable()

                // Update history state
                const state = globeHistoryState({
                    fips: this.COUNTY_GROUP.userData.fips,
                })
                updateHistory(state)
            }
        }
    }

    performMapTextureSwap(mesh: THREE.Mesh, mapLevel: number): void {
        if (mesh.parent === null) {
            console.error("Mesh had no parent")
            return
        }
        let materials
        if (
            mesh.userData.inactive !== undefined &&
            !(mesh.userData.highlighted as boolean)
        ) {
            materials = mesh.parent.userData.materials.inactive
        } else {
            materials = mesh.parent.userData.materials.active
        }
        mesh.material = materials[mapLevel]
    }

    // TODO: Refactor to a math library
    isInBounds(
        val: number,
        low: number,
        high: number,
        threshold: number
    ): boolean {
        return val > low - threshold && val < high + threshold
    }

    /**
     * Remove a group of counties
     * Also disposes of county geometry, labels and textures
     */
    removeCountyGroup(countyGroup: THREE.Object3D): void {
        countyGroup.traverse((obj) => {
            // Dispose geometry
            if (
                "geometry" in obj &&
                obj.geometry instanceof THREE.BufferGeometry
            )
                obj.geometry.dispose()

            // Dispose material
            if ("material" in obj && obj.material instanceof THREE.Material) {
                if (
                    "map" in obj.material &&
                    obj.material.map instanceof THREE.Texture
                ) {
                    obj.material.map.dispose()
                }
                obj.material.dispose()
            }

            // Remove label
            const label = this.getLabel(obj)
            if (label != null) {
                label.parent?.remove(label)
            }
        })

        countyGroup.parent?.remove(countyGroup)
        countyGroup.remove(...countyGroup.children)
        this.updateIntersectable()
        // this.COUNTY_GROUP = new THREE.Group();
    }

    onZoomLevelChange(event): void {
        this.updateIntersectable()
        const navList = document.querySelector(".navigation_list")
        const bottomCurves = document.querySelectorAll<HTMLImageElement>(
            ".curve-left, .curve-right, img.l, img.r"
        )
        /// //////////////////////////////////////////
        // Handle going from zoomed out to zoomed in
        /// //////////////////////////////////////////
        if (
            event.detail.oldZoomLevel === 0 &&
            event.detail.newZoomLevel === 1
        ) {
            // Show the reset button
            if (!this.state.resetButtonShown) {
                this.resetButton.classList.remove("hidden")
                this.resetButton.classList.add("visible")
                this.state.resetButtonShown = true
            }
            // Do stuff
            void this.loadUSAPromise.then(() => {
                if (this.USA.userData.state === "visible") {
                    this.USA.userData.state = "hidden"
                    // Stop all current tweens
                    getTweenGroup(this.USA).removeAll()
                    this.USA.traverse((obj) => {
                        if (!(obj instanceof THREE.Mesh)) return

                        const tween1 = new Tween(
                            obj.material,
                            getTweenGroup(this.USA)
                        )
                            .to({ opacity: 0.5 }, 120)
                            .onComplete(() => {
                                obj.castShadow = false
                                obj.receiveShadow = false
                            })
                        const tween2 = new Tween(
                            obj.material,
                            getTweenGroup(this.USA)
                        )
                            .to({ opacity: 0 }, 120)
                            .onComplete(() => {
                                this.USA.visible = false
                            })
                        tween1.chain(tween2)
                        tween1.start()
                    })
                    if (!this.areCountiesLoaded()) {
                        // Raise the states
                        this.STATE_GROUP.traverse((obj) => {
                            // Filter out all but top level (face) meshes
                            if (obj.parent !== this.STATE_GROUP) return
                            new Tween(obj.scale, getTweenGroup(obj))
                                .to(obj.userData.randomScale, 200)
                                .start()
                        })
                    }
                }
            })

            // Change the color of the countries
            this.COUNTRIES.children[0].children
                .filter((child) => isFace(child, this.COUNTRIES.children[0]))
                .forEach((face: THREE.Mesh) => {
                    if (!("color" in face.material)) {
                        console.warn("Mesh material did not have color")
                        return
                    }
                    const color = face.material.color as THREE.Color
                    color.set("#141a1f")
                    if (!("emissive" in face.material)) {
                        // This is the case for basic materials
                        return
                    }
                    const emissive = face.material.emissive as THREE.Color
                    emissive.set("#141a1f")
                })
            this.tutorial.state.send("ZOOM_IN")

            // Show the bottom bar
            if (navList !== null) {
                navList.classList.remove("hidden")
            } else {
                console.error("navList was null")
            }

            this.dispatchFooterChangeEvent()
            // Hide the curves on the overlay to make the bottom bar blend in better
            bottomCurves.forEach((element) => {
                element.style.display = "none"
            })
        }
        /// //////////////////////////////////////////
        // End handling going from zoomed out to zoomed in
        /// //////////////////////////////////////////

        /// //////////////////////////////////////////
        // Handle going from zoomed in to zoomed out
        /// //////////////////////////////////////////
        if (
            event.detail.oldZoomLevel === 1 &&
            event.detail.newZoomLevel === 0
        ) {
            if (this.USA.userData.state === "hidden") {
                this.USA.visible = true
                this.USA.userData.state = "visible"
                // Stop all current tweens
                getTweenGroup(this.USA).removeAll()
                this.USA.traverse((obj) => {
                    if (!(obj instanceof THREE.Mesh)) return

                    const tween1 = new Tween(
                        obj.material,
                        getTweenGroup(this.USA)
                    )
                        .to({ opacity: 0.5 }, 50)
                        .onComplete(() => {
                            obj.receiveShadow = true
                            if (obj.parent === this.USA) {
                                obj.castShadow = true
                            }
                        })
                    const tween2 = new Tween(
                        obj.material,
                        getTweenGroup(this.USA)
                    ).to({ opacity: 1 }, 50)
                    tween1.chain(tween2)

                    tween1.start()
                })

                // Lower the states
                this.STATE_GROUP.traverse((obj) => {
                    // Filter out all but top level (face) meshes
                    if (obj.parent !== this.STATE_GROUP) return

                    this.ANIMATIONS.add(
                        new Animation(
                            obj,
                            "scale",
                            obj.userData.hiddenScale,
                            0.2,
                            0
                        ).setEasing(easeOut)
                    )
                })
            }

            // Change the colors of the countries
            this.COUNTRIES.children[0].children
                .filter((child) => isFace(child, this.COUNTRIES.children[0]))
                .forEach((face: THREE.Mesh) => {
                    if (!("color" in face.material)) {
                        console.warn("Mesh material did not have color")
                        return
                    }
                    const color = face.material.color as THREE.Color
                    color.copy(face.userData.baseColor)
                    if (!("emissive" in face.material)) {
                        // This is the case for basic materials
                        return
                    }
                    const emissive = face.material.emissive as THREE.Color
                    emissive.copy(face.userData.baseEmissive)
                })

            this.tutorial.state.send("ZOOM_OUT")

            // Hide the bottom bar
            if (navList !== null) {
                navList.classList.add("hidden")
            } else {
                console.error("navList was null")
            }

            this.dispatchFooterChangeEvent()
            // repositionBottomMenuCurves();

            // Bring back the curves on the overlay
            bottomCurves.forEach((element) => {
                element.style.display = "initial"
            })
        }
    }

    getStateByAbbr(abbr: string): null | THREE.Object3D {
        const nChildren = this.STATE_GROUP.children.length
        for (let i = 0; i < nChildren; i++) {
            const state = this.STATE_GROUP.children[i]
            if (state.userData.abbr === abbr) {
                return state
            }
        }
        return null
    }

    getStateByFIPS(fips: string): THREE.Mesh {
        return this.getStateByName(STATE_INFO_BY_FIPS[fips].name)
    }

    onCameraChange(): void {
        const dist = this.controls.getDistance()
        this.controls.rotateSpeed = (dist - this.controls.surfaceDist) * 0.0035

        // Set camera far clip (optimization attempt)
        this.camera.far = dist
        this.camera.updateProjectionMatrix()

        // Short circuit if countries aren't loaded yet
        if (this.COUNTRIES.children.length === 0) return

        let newZoomLevel

        // Set the zoom level depending on how far away we are
        if (dist >= 140) {
            newZoomLevel = 0
        } else {
            newZoomLevel = 1
        }

        // Cancel the zoom level change if we're zooming all the way out and a state has been clicked
        if (this.state.aStateHasBeenClicked && newZoomLevel === 0) {
            newZoomLevel = this.state.zoomLevel
        }

        // Dispatch a zoomlevelchanged event if the new zoom level is different from the old one
        if (this.state.zoomLevel !== newZoomLevel) {
            const oldZoomLevel = this.state.zoomLevel
            this.state.zoomLevel = newZoomLevel
            this.dispatchEvent(
                new CustomEvent("zoomlevelchange", {
                    detail: { oldZoomLevel, newZoomLevel },
                })
            )
        }
    }

    dispatchFooterChangeEvent(): void {
        dispatchFooterChangeEvent()
    }

    _optimizationHideCountries(): void {
        const exceptions = [
            "Mexico",
            "Canada",
            "Cuba",
            "Jamaica",
            "Haiti",
            "Dominican_Republic",
            "Guatemala",
            "Honduras",
            "El_Salvador",
            "Nicaragua",
            "Costa_Rica",
            "Panama",
            "Russian_Federation",
            "Greenland",
            "Iceland",
            "Colombia",
            "Venezuela",
            "Ecuador",
            "Guyana",
            "Suriname",
            "French_Guiana",
            "Peru",
            "Brazil",
            "Bolivia",
            "Paraguay",
            "Chile",
            "Argentina",
            "Uruguay",
        ]
        void this.loadCountriesPromise.then(() => {
            this.COUNTRIES.children[0].children.forEach((country) => {
                // console.log(country)
                // Short circuit if country in exceptions
                if (exceptions.includes(country.name)) return
                // Hide the country
                country.visible = false
            })
        })
    }

    _optimizationShowCountries(): void {
        this.COUNTRIES.children[0].children.forEach((country) => {
            // Hide the country
            country.visible = true
        })
    }
}

function makeVisible(region: Region): void {
    // Face shadows: Recieve, but don't cast
    region.face.receiveShadow = true
    // Bottom border shadows
    region.bottomBorder.receiveShadow = true
    region.bottomBorder.castShadow = true
    // Top border shadows
    region.topBorder.castShadow = true
    if (region.face.userData.type !== "county") {
        region.topBorder.receiveShadow = true
    }
    region.face.visible = true
    region.bottomBorder.visible = true
    region.topBorder.visible = true
}
